"use client";

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import FormInputText from '../input-text';
import CustomImage from '@/components/image';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const FULL_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MIN_SELECTABLE_YEAR = 1900;
const MAX_FUTURE_YEAR_OFFSET = 100;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse "mm/dd/yyyy" → Date or null */
function parseDate(str) {
  if (!str || !/^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/.test(str)) return null;
  const [mm, dd, yyyy] = str.split('/');
  const d = new Date(+yyyy, +mm - 1, +dd);
  // Guard invalid calendar dates (e.g. 02/31/2025)
  if (d.getMonth() !== +mm - 1) return null;
  return d;
}

/** Date → "mm/dd/yyyy" */
function formatDate(date) {
  const mm   = String(date.getMonth() + 1).padStart(2, '0');
  const dd   = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function getStartOfToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function toStartOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isFutureDate(date) {
  return toStartOfDay(date) > getStartOfToday();
}

function isPastDate(date) {
  return toStartOfDay(date) < getStartOfToday();
}

function isDateDisabledByMode(date, mode) {
  if (mode === 'future') return toStartOfDay(date) >= getStartOfToday();
  if (mode === 'past') return isPastDate(date);
  return false;
}

/** Returns true when a calendar day cell is today's date (outside-month days excluded) */
function isTodayDay(day) {
  if (day.outside) return false;
  const t = new Date();
  return day.day === t.getDate() && day.month === t.getMonth() && day.year === t.getFullYear();
}

// ---------------------------------------------------------------------------
// SVG icons
// ---------------------------------------------------------------------------

function CalendarIcon() {
  return (
    <CustomImage
      src="/images/components/input-calendar/input-calendar-icon.png"
      alt=""
      width={40}
      height={40}
      aria-hidden="true"
    />
  );
}

/** ↑ shown in input button when calendar panel is open */
function ChevronUp() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_648_144)">
        <path d="M31.8555 24.6459L20.011 14.2749L8.16641 24.6459L9.64717 25.9422L20.011 16.8676L30.3749 25.9422L31.8555 24.6459Z" fill="#7C367B"/>
      </g>
      <defs>
        <clipPath id="clip0_648_144">
        <path d="M32.2129 14.25C32.2129 14.1119 32.101 14 31.9629 14L8.25063 14C8.11256 14 8.00063 14.1119 8.00063 14.25L8.00063 26L32.2129 26L32.2129 14.25Z" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );
}

/** ↑ nav arrow: previous month */
function NavArrowUp() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* arrowhead */}
      <path d="M8 22L20 10L32 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* shaft */}
      <path d="M20 10L20 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** ↓ nav arrow: next month */
function NavArrowDown() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* shaft */}
      <path d="M20 10L20 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* arrowhead */}
      <path d="M8 18L20 30L32 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @description Date input with inline calendar picker.
 * Extends FormInputText — all FormInputText props are supported.
 * Adds a calendar icon that toggles an inline date-picker panel.
 * Input is auto-formatted as mm/dd/yyyy and validated as a real date.
 *
 * @param {string}   name          — field name
 * @param {string}   label         — field label
 * @param {boolean}  [required]    — required validation
 * @param {string}   [defaultValue]— initial value "mm/dd/yyyy"
 * @param {'future'|'past'} [disableDates] — `future`: allow only dates before today; `past`: allow today and future dates
 * @param {boolean}  [disableFutureDates] — deprecated alias for disableDates="future"
 * @param {number}   [yearDropdownYearsBack]  — number of years before current year to include in year dropdown
 * @param {number}   [yearDropdownYearsAhead] — number of years after current year to include in year dropdown
 * @param {Function} [onChange]    — called with synthetic event on value change
 * @param {string}   [customClass] — extra class on the outer wrapper
 */
const FormInputCalendar = forwardRef(function FormInputCalendar(props, ref) {
  const {
    name,
    label,
    required = false,
    defaultValue = '',
    disableDates,
    disableFutureDates = false,
    yearDropdownYearsBack,
    yearDropdownYearsAhead,
    onChange,
    errorMessage = 'Please enter a valid date (mm/dd/yyyy).',
    customClass,
    ...rest
  } = props;

  const effectiveDisableDates = disableDates ?? (disableFutureDates ? 'future' : undefined);

  const inputTextRef     = useRef(null);
  const wrapperRef       = useRef(null);
  const inputWithIconRef = useRef(null);
  const panelRef         = useRef(null);
  const iconBtnRef       = useRef(null);
  const wasOpenRef       = useRef(false);
  // When true the next focusedDay state change will move DOM focus to that day button
  const shouldFocusDayRef = useRef(false);

  const [isOpen, setIsOpen]             = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const parsed = parseDate(defaultValue);
    if (!parsed || !isDateDisabledByMode(parsed, effectiveDisableDates)) return parsed;
    return null;
  });
  const [hasExplicitSelection, setHasExplicitSelection] = useState(() => {
    const parsed = parseDate(defaultValue);
    return !!parsed && !isDateDisabledByMode(parsed, effectiveDisableDates);
  });
  const [viewDate, setViewDate]         = useState(() => {
    const parsed = parseDate(defaultValue);
    if (!parsed || !isDateDisabledByMode(parsed, effectiveDisableDates)) return parsed ?? new Date();
    return getStartOfToday();
  });
  // Tracks which day cell owns tabIndex=0 (roving tabindex pattern)
  const [focusedDay, setFocusedDay]     = useState(null);
  // Drives our own required-validation display (FormInputText never sees required)
  const [manualInvalid, setManualInvalid] = useState(false);
  // Warning-only state — shows error message without changing the input border color
  const [manualWarning, setManualWarning] = useState(false);
  // Dynamic right inset so icon stays aligned to the actual input edge,
  // even when custom styles constrain input width inside a wider wrapper.
  const [iconRightOffset, setIconRightOffset] = useState(0);
  // Tracks whether invalid state is controlled externally via setInvalid()
  const externalInvalidRef = useRef(false);
  // Skip the next handleInputChange (fired by browser's native change-on-blur after programmatic setValue)
  const skipNextChangeRef = useRef(false);

  const canGoToPrevMonth = useMemo(() => {
    if (effectiveDisableDates !== 'past') return true;
    const viewMonthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const today = getStartOfToday();
    const todayMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return viewMonthStart > todayMonthStart;
  }, [effectiveDisableDates, viewDate]);

  const canGoToNextMonth = useMemo(() => {
    if (effectiveDisableDates !== 'future') return true;
    const viewMonthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const today = getStartOfToday();
    const todayMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return viewMonthStart < todayMonthStart;
  }, [effectiveDisableDates, viewDate]);

  const canSelectToday = useMemo(() => {
    return !isDateDisabledByMode(getStartOfToday(), effectiveDisableDates);
  }, [effectiveDisableDates]);

  const yearOptions = useMemo(() => {
    const currentYear = getStartOfToday().getFullYear();
    const hasCustomYearsBack = Number.isFinite(yearDropdownYearsBack) && yearDropdownYearsBack >= 0;
    const customMinYear = hasCustomYearsBack
      ? currentYear - Math.floor(yearDropdownYearsBack)
      : null;
    const hasCustomYearsAhead = Number.isFinite(yearDropdownYearsAhead) && yearDropdownYearsAhead >= 0;
    const customMaxYear = hasCustomYearsAhead
      ? currentYear + Math.floor(yearDropdownYearsAhead)
      : null;
    const yearsAheadForPastMode = effectiveDisableDates === 'past' ? 1 : MAX_FUTURE_YEAR_OFFSET;

    let minYear = effectiveDisableDates === 'past'
      ? currentYear
      : hasCustomYearsBack
        ? Math.min(customMinYear, viewDate.getFullYear())
        : Math.min(MIN_SELECTABLE_YEAR, viewDate.getFullYear());
    let maxYear = effectiveDisableDates === 'future'
      ? currentYear
      : hasCustomYearsAhead
        ? Math.max(customMaxYear, viewDate.getFullYear())
        : Math.max(currentYear + yearsAheadForPastMode, viewDate.getFullYear());

    if (minYear > maxYear) {
      minYear = maxYear;
    }

    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  }, [effectiveDisableDates, viewDate, yearDropdownYearsBack, yearDropdownYearsAhead]);

  const isDayDisabled = useCallback((day) => {
    if (day.outside) return true;
    return isDateDisabledByMode(new Date(day.year, day.month, day.day), effectiveDisableDates);
  }, [effectiveDisableDates]);

  // ── Toggle calendar open/closed ─────────────────────────────────────────
  const handleToggle = useCallback(() => {
    if (!isOpen) {
      let base = selectedDate;
      
      // If no selected date yet, pick a sensible default based on the mode
      if (!base) {
        if (effectiveDisableDates === 'future') {
          // 'future' mode: allow only past dates, so default to yesterday
          base = new Date(getStartOfToday());
          base.setDate(base.getDate() - 1);
        } else if (effectiveDisableDates === 'past') {
          // 'past' mode: allow only future dates, so default to tomorrow
          base = new Date(getStartOfToday());
          base.setDate(base.getDate() + 1);
        } else {
          // No restriction, default to today
          base = getStartOfToday();
        }
        
        // If the computed base is disabled, fall back to today
        if (isDateDisabledByMode(base, effectiveDisableDates)) {
          base = getStartOfToday();
        }
        
        // Pre-fill this default date in the input and keep internal date state,
        // but do not mark it as an explicit visual selection.
        const formatted = formatDate(base);
        setSelectedDate(base);
        setHasExplicitSelection(false);
        skipNextChangeRef.current = true;
        if (inputTextRef.current) {
          inputTextRef.current.setValue(formatted);
        }
        if (onChange) onChange({ target: { value: formatted, name } });
      }
      
      setViewDate(base ?? selectedDate ?? getStartOfToday());
      shouldFocusDayRef.current = true;
      const focusDate = base ?? selectedDate ?? getStartOfToday();
      setFocusedDay({ day: focusDate.getDate(), month: focusDate.getMonth(), year: focusDate.getFullYear() });
    }
    setIsOpen(v => !v);
  }, [effectiveDisableDates, isOpen, selectedDate, onChange, name]);

  // ── Close on outside click ──────────────────────────────────────────────
  useEffect(() => {
    function onOutsideClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, []);

  // ── Close on Escape ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    function onKeydown(e) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', onKeydown);
    return () => document.removeEventListener('keydown', onKeydown);
  }, [isOpen]);

  // ── Move DOM focus to the focused day button when it changes ─────────────
  useEffect(() => {
    if (!isOpen || !focusedDay || !panelRef.current || !shouldFocusDayRef.current) return;
    shouldFocusDayRef.current = false;
    const btn = panelRef.current.querySelector(
      `[data-day="${focusedDay.year}-${focusedDay.month}-${focusedDay.day}"]`
    );
    btn?.focus();
  }, [isOpen, focusedDay]);

  // ── Return focus to icon button when the panel closes ────────────────────
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      // Only steal focus back if it landed on body or stayed inside our widget
      const ae = document.activeElement;
      if (!ae || ae === document.body || wrapperRef.current?.contains(ae)) {
        iconBtnRef.current?.focus();
      }
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // ── Sync selectedDate when user types a valid date in the input ──────────
  const handleInputChange = useCallback((e) => {
    if (skipNextChangeRef.current) {
      skipNextChangeRef.current = false;
      return;
    }
    const parsed = parseDate(e.target.value);
    if (parsed) {
      if (isDateDisabledByMode(parsed, effectiveDisableDates)) {
        setSelectedDate(null);
        setHasExplicitSelection(false);
        setViewDate(getStartOfToday());
      } else {
        setSelectedDate(parsed);
        setHasExplicitSelection(true);
        setViewDate(parsed);
      }
    }
    if (onChange) onChange(e);
    // Auto-clear required-field error only when not externally controlled
    if (parsed && !externalInvalidRef.current) {
      setManualInvalid(false);
    }
  }, [effectiveDisableDates, onChange]);

  // ── Keep icon aligned to input's right edge (not wrapper edge) ────────────
  useEffect(() => {
    function updateIconOffset() {
      const host = inputWithIconRef.current;
      if (!host) return;
      const inputEl = host.querySelector('.form-input-text__wrapper > input');
      if (!inputEl) return;

      const hostRect = host.getBoundingClientRect();
      const inputRect = inputEl.getBoundingClientRect();
      const next = Math.max(0, Math.round(hostRect.right - inputRect.right));
      setIconRightOffset((prev) => (prev === next ? prev : next));
    }

    updateIconOffset();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => updateIconOffset());
      if (inputWithIconRef.current) ro.observe(inputWithIconRef.current);
      const inputEl = inputWithIconRef.current?.querySelector('.form-input-text__wrapper > input');
      if (inputEl) ro.observe(inputEl);
      window.addEventListener('resize', updateIconOffset);
      return () => {
        ro.disconnect();
        window.removeEventListener('resize', updateIconOffset);
      };
    }

    window.addEventListener('resize', updateIconOffset);
    return () => window.removeEventListener('resize', updateIconOffset);
  }, [defaultValue, label]);

  // ── Calendar grid ───────────────────────────────────────────────────────
  const calendarDays = useMemo(() => {
    const year     = viewDate.getFullYear();
    const month    = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();      // 0 = Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const prevMonth = month === 0  ? 11 : month - 1;
    const prevYear  = month === 0  ? year - 1 : year;
    const nextMonth = month === 11 ? 0  : month + 1;
    const nextYear  = month === 11 ? year + 1 : year;
    const prevMonthDays = new Date(year, month, 0).getDate();

    const days = [];

    // Leading days from previous month
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthDays - i, month: prevMonth, year: prevYear, outside: true });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ day: d, month, year, outside: false });
    }
    // Trailing days to always fill 6 complete weeks (42 cells total).
    // This keeps the grid height constant regardless of the month layout,
    // preventing page scroll jumps when navigating between months.
    let nextDay = 1;
    while (days.length < 42) {
      days.push({ day: nextDay++, month: nextMonth, year: nextYear, outside: true });
    }

    return days;
  }, [viewDate]);

  const isSelected = useCallback((day) => {
    if (!selectedDate || !hasExplicitSelection) return false;
    return (
      selectedDate.getFullYear() === day.year &&
      selectedDate.getMonth()    === day.month &&
      selectedDate.getDate()     === day.day
    );
  }, [hasExplicitSelection, selectedDate]);

  // ── Day selection ────────────────────────────────────────────────────────
  const handleDaySelect = useCallback((day) => {
    if (isDayDisabled(day)) return;
    const date      = new Date(day.year, day.month, day.day);
    const formatted = formatDate(date);
    setSelectedDate(date);
    setHasExplicitSelection(true);
    setViewDate(date);
    setIsOpen(false);
    skipNextChangeRef.current = true;
    if (inputTextRef.current) {
      inputTextRef.current.setValue(formatted);
    }
    if (onChange) onChange({ target: { value: formatted, name } });
    // Auto-clear required-field error only when not externally controlled
    if (!externalInvalidRef.current) {
      setManualInvalid(false);
    }
  }, [isDayDisabled, onChange, name]);

  const goToPrevMonth = useCallback(() => {
    if (!canGoToPrevMonth) return;
    setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    // Keep roving-tabindex day in the new month (no DOM-focus steal)
    setFocusedDay(fd => {
      if (!fd) return fd;
      const nm = fd.month === 0 ? 11 : fd.month - 1;
      const ny = fd.month === 0 ? fd.year - 1 : fd.year;
      const max = new Date(ny, nm + 1, 0).getDate();
      return { day: Math.min(fd.day, max), month: nm, year: ny };
    });
  }, [canGoToPrevMonth]);

  const goToNextMonth = useCallback(() => {
    if (!canGoToNextMonth) return;
    setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    setFocusedDay(fd => {
      if (!fd) return fd;
      const nm = fd.month === 11 ? 0  : fd.month + 1;
      const ny = fd.month === 11 ? fd.year + 1 : fd.year;
      const max = new Date(ny, nm + 1, 0).getDate();
      return { day: Math.min(fd.day, max), month: nm, year: ny };
    });
  }, [canGoToNextMonth]);

  // ── Clear selection ──────────────────────────────────────────────────────
  const handleClear = useCallback(() => {
    setSelectedDate(null);
    setHasExplicitSelection(false);
    setIsOpen(false);
    skipNextChangeRef.current = true;
    if (inputTextRef.current) {
      inputTextRef.current.setValue('');
    }
    if (onChange) onChange({ target: { value: '', name } });
    if (!externalInvalidRef.current) {
      setManualInvalid(false);
    }
  }, [onChange, name]);

  // ── Select today ─────────────────────────────────────────────────────────
  const handleSelectToday = useCallback(() => {
    const today     = new Date();
    const formatted = formatDate(today);
    setSelectedDate(today);
    setHasExplicitSelection(true);
    setViewDate(today);
    setIsOpen(false);
    skipNextChangeRef.current = true;
    if (inputTextRef.current) {
      inputTextRef.current.setValue(formatted);
    }
    if (onChange) onChange({ target: { value: formatted, name } });
    if (!externalInvalidRef.current) {
      setManualInvalid(false);
    }
  }, [onChange, name]);

  // ── Tab trap: keep focus cycling within the open panel ─────────────────
  const handlePanelKeyDown = useCallback((e) => {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      panelRef.current?.querySelectorAll('button, select') ?? []
    ).filter(el => !el.disabled && el.tabIndex >= 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  }, []);

  // ── Arrow-key navigation within the grid ─────────────────────────────────
  const handleDayKeyDown = useCallback((e, day) => {
    const cur = new Date(day.year, day.month, day.day);
    let next;
    switch (e.key) {
      case 'ArrowLeft':  e.preventDefault(); next = new Date(cur); next.setDate(cur.getDate() - 1); break;
      case 'ArrowRight': e.preventDefault(); next = new Date(cur); next.setDate(cur.getDate() + 1); break;
      case 'ArrowUp':    e.preventDefault(); next = new Date(cur); next.setDate(cur.getDate() - 7); break;
      case 'ArrowDown':  e.preventDefault(); next = new Date(cur); next.setDate(cur.getDate() + 7); break;
      case 'PageUp':     e.preventDefault(); next = new Date(cur.getFullYear(), cur.getMonth() - 1, cur.getDate()); break;
      case 'PageDown':   e.preventDefault(); next = new Date(cur.getFullYear(), cur.getMonth() + 1, cur.getDate()); break;
      case 'Home':       e.preventDefault(); next = new Date(cur); next.setDate(cur.getDate() - cur.getDay()); break;
      case 'End':        e.preventDefault(); next = new Date(cur); next.setDate(cur.getDate() + (6 - cur.getDay())); break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!day.outside) handleDaySelect(day);
        return;
      default: return;
    }
    if (next) {
      if (isDateDisabledByMode(next, effectiveDisableDates)) {
        next = getStartOfToday();
      }
      shouldFocusDayRef.current = true;
      const nd = { day: next.getDate(), month: next.getMonth(), year: next.getFullYear() };
      setFocusedDay(nd);
      // Cross-month navigation: update the visible month
      if (next.getMonth() !== day.month || next.getFullYear() !== day.year) {
        setViewDate(new Date(next.getFullYear(), next.getMonth(), 1));
      }
    }
  }, [effectiveDisableDates, handleDaySelect]);

  // Group flat calendarDays into rows of 7 for grid/row ARIA structure
  const calendarRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < calendarDays.length; i += 7) rows.push(calendarDays.slice(i, i + 7));
    return rows;
  }, [calendarDays]);

  // ── Forward validation API ───────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    validate: () => {
      const val = inputTextRef.current?.value ?? '';
      if (required && !val) {
        setManualInvalid(true);
        return false;
      }
      setManualInvalid(false);
      // Delegate format validation to FormInputText (autoformatDate regex)
      return inputTextRef.current?.validate() ?? true;
    },
    testValidation: () => {
      const val = inputTextRef.current?.value ?? '';
      if (required && !val) return false;
      return inputTextRef.current?.testValidation() ?? true;
    },
    get name()  { return name; },
    get valid() {
      const val = inputTextRef.current?.value ?? '';
      if (required && !val) return false;
      return inputTextRef.current?.valid ?? true;
    },
    get value() { return inputTextRef.current?.value; },
    setValue:    (val) => { inputTextRef.current?.setValue(val); },
    setInvalid:  (flag) => { externalInvalidRef.current = flag; setManualInvalid(flag); if (flag) setManualWarning(false); },
    setWarning:  (flag) => { setManualWarning(flag); },
    reset:       () => { inputTextRef.current?.reset(); setSelectedDate(null); setHasExplicitSelection(false); externalInvalidRef.current = false; setManualInvalid(false); setManualWarning(false); },
    get type()   { return 'calendar'; },
  }), [required, name]);

  return (
    <div ref={wrapperRef} className={`form-input-calendar${customClass ? ` ${customClass}` : ''}`}>

      {/* ── Text input + calendar icon ─────────────────────────────────── */}
      <div
        ref={inputWithIconRef}
        className={`form-input-calendar__input-with-icon${isOpen ? ' form-input-calendar__input-with-icon--open' : ''}`}
      >
        <div className="form-input-calendar__input-row">
          <FormInputText
            ref={inputTextRef}
            name={name}
            label={label}
            customClass={manualInvalid ? 'form-input-text--invalid' : undefined}
            autoformatDate
            maxLength={10}
            placeholder="mm/dd/yyyy"
            inputMode="numeric"
            defaultValue={defaultValue}
            onChange={handleInputChange}
            errorMessage={errorMessage}
            {...rest}
          />
          <button
            ref={iconBtnRef}
            type="button"
            className="form-input-calendar__icon-btn"
            style={{ right: `${iconRightOffset}px` }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleToggle}
            aria-label={isOpen ? 'Close date picker' : 'Open date picker'}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
          >
            {isOpen ? <ChevronUp /> : <CalendarIcon />}
          </button>
        </div>

        {(manualInvalid || manualWarning) && (
          <div role="alert" className="invalid-feedback text-sm mt-1">
            {errorMessage}
          </div>
        )}
      </div>

      {/* ── Calendar panel ────────────────────────────────────────────────── */}
      {isOpen && (
        <div
          ref={panelRef}
          className="form-input-calendar__panel"
          role="dialog"
          aria-label="Date picker"
          aria-modal="true"
          onKeyDown={handlePanelKeyDown}
        >

          {/* Month/year label (left) + nav arrows (right) */}
          <div className="form-input-calendar__nav-header">
            <div
              className="form-input-calendar__month-year-wrapper"
              aria-live="polite"
              aria-atomic="true"
            >
              <span className="form-input-calendar__month-label">{MONTH_NAMES[viewDate.getMonth()]}</span>
              <select
                className="form-input-calendar__year-select"
                value={viewDate.getFullYear()}
                onChange={(e) => setViewDate(d => new Date(+e.target.value, d.getMonth(), 1))}
                aria-label="Select year"
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="form-input-calendar__nav-arrows">
              <button
                type="button"
                className="form-input-calendar__nav-btn"
                onClick={goToPrevMonth}
                aria-label="Previous month"
                disabled={!canGoToPrevMonth}
              >
                <NavArrowUp />
              </button>
              <button
                type="button"
                className="form-input-calendar__nav-btn"
                onClick={goToNextMonth}
                aria-label="Next month"
                disabled={!canGoToNextMonth}
              >
                <NavArrowDown />
              </button>
            </div>
          </div>

          {/* Days grid — role="grid" with row/columnheader/gridcell for screen readers */}
          <div
            role="grid"
            className="form-input-calendar__days-grid"
            aria-label={`${MONTH_NAMES[viewDate.getMonth()]} ${viewDate.getFullYear()}`}
          >

            {/* Column headers row */}
            <div role="row" className="form-input-calendar__days-row">
              {DAYS_OF_WEEK.map((d, i) => (
                <div
                  key={i}
                  role="columnheader"
                  className="form-input-calendar__day-header"
                  aria-label={FULL_DAY_NAMES[i]}
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Week rows */}
            {calendarRows.map((row, ri) => (
              <div key={ri} role="row" className="form-input-calendar__days-row">
                {row.map((day, di) => {
                  const disabled = isDayDisabled(day);
                  const isFocused = !!focusedDay &&
                    focusedDay.year  === day.year  &&
                    focusedDay.month === day.month &&
                    focusedDay.day   === day.day;
                  return (
                    <button
                      key={di}
                      role="gridcell"
                      type="button"
                      data-day={`${day.year}-${day.month}-${day.day}`}
                      className={[
                        'form-input-calendar__day-btn',
                        day.outside                       ? 'form-input-calendar__day-btn--outside'  : '',
                        isSelected(day)                   ? 'form-input-calendar__day-btn--selected' : '',
                        isTodayDay(day)                   ? 'form-input-calendar__day-btn--today'    : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => !disabled && handleDaySelect(day)}
                      onKeyDown={(e) => handleDayKeyDown(e, day)}
                      onFocus={() => setFocusedDay({ day: day.day, month: day.month, year: day.year })}
                      aria-label={`${MONTH_NAMES[day.month]} ${day.day}, ${day.year}`}
                      aria-selected={isSelected(day)}
                      aria-disabled={disabled || undefined}
                      tabIndex={isFocused ? 0 : -1}
                    >
                      {day.day}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer — Clear / Today */}
          <div className="form-input-calendar__footer">
            <button type="button" className="form-input-calendar__footer-clear" onClick={handleClear}>
              Clear
            </button>
            {canSelectToday && (
              <button type="button" className="form-input-calendar__footer-today" onClick={handleSelectToday}>
                Today
              </button>
            )}
          </div>

        </div>
      )}
    </div>
  );
});

export default FormInputCalendar;
